import React, { useState, useRef, ChangeEvent, useEffect } from "react";
import { Row, Col, Image } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";

interface ImageItem {
    url: string;
    file?: File;
    isInitial: boolean;
}

interface MultiImageUploaderProps {
    initialImages?: string[];
    onImagesChange: (urls: string[], files: File[]) => void;
    label?: string;
}

const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({
    initialImages = [],
    onImagesChange,
    label = "Upload images",
}) => {
    const [images, setImages] = useState<ImageItem[]>(() =>
        initialImages.map((url) => ({
            url,
            isInitial: true,
        }))
    );
    const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const onImagesChangeRef = useRef(onImagesChange);
    const isInitialMount = useRef(true);

    // Keep the ref updated with the latest callback
    useEffect(() => {
        onImagesChangeRef.current = onImagesChange;
    }, [onImagesChange]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const urls = images.filter((img) => img.isInitial).map((img) => img.url);
        const files = images.filter((img) => !img.isInitial && img.file).map((img) => img.file!);
        onImagesChangeRef.current(urls, files);
    }, [images]);

    const handleBoxClick = (index: number) => {
        fileInputRefs.current[index]?.click();
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        const reader = new FileReader();
        reader.onloadend = () => {
            const newImage: ImageItem = {
                url: reader.result as string,
                file: selectedFile,
                isInitial: false,
            };

            setImages((prev) => {
                const updated = [...prev];
                if (index < updated.length) {
                    updated[index] = newImage;
                } else {
                    updated.push(newImage);
                }
                return updated;
            });
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleRemove = (index: number) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const totalSlots = images.length + 1;

    return (
        <div className="my-3">
            <label>{label}</label>
            <Row>
                {Array.from({ length: totalSlots }).map((_, index) => {
                    const image = images[index];
                    const hasImage = !!image;

                    return (
                        <Col
                            key={index}
                            xs={6}
                            md={4}
                            className="mb-3"
                            data-testid={`image-upload-col-${index}`}
                        >
                            <div style={{ position: "relative" }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    ref={(el) => (fileInputRefs.current[index] = el)}
                                    onChange={(e) => handleFileChange(e, index)}
                                    data-testid={`image-upload-input-${index}`}
                                />
                                <div
                                    onClick={() => !hasImage && handleBoxClick(index)}
                                    style={{
                                        width: "100%",
                                        aspectRatio: "1/1",
                                        backgroundColor: "#dee2e6",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "0.25rem",
                                        border: "1px solid #ced4da",
                                        cursor: hasImage ? "default" : "pointer",
                                        overflow: "hidden",
                                    }}
                                    data-testid={`image-upload-box-${index}`}
                                >
                                    {hasImage ? (
                                        <Image
                                            src={image.url}
                                            alt={`Image ${index + 1}`}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : (
                                        <span
                                            style={{
                                                fontWeight: "bold",
                                                color: "#6c757d",
                                                fontSize: "2rem",
                                            }}
                                        >
                                            +
                                        </span>
                                    )}
                                </div>
                                {hasImage && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(index)}
                                        style={{
                                            position: "absolute",
                                            top: "0.25rem",
                                            right: "0.25rem",
                                            backgroundColor: "rgba(220, 53, 69, 0.9)",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "50%",
                                            width: "24px",
                                            height: "24px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            cursor: "pointer",
                                            padding: 0,
                                        }}
                                        data-testid={`image-remove-button-${index}`}
                                    >
                                        <FaTimes size={12} />
                                    </button>
                                )}
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default MultiImageUploader;
